import React from 'react';
import { X, ExternalLink, Download } from 'lucide-react';

const FileViewer = ({ file, isOpen, onClose }) => {
    if (!isOpen || !file) return null;

    const getFileContent = () => {
        const fileType = file.file_type || file.type; // Handle both backend and frontend file objects
        const fileUrl = file.file_url || (file instanceof File ? URL.createObjectURL(file) : null);
        const fileName = file.file_name || file.name;

        if (!fileUrl) return <div className="text-center p-4">Cannot preview this file.</div>;

        if (fileType.startsWith('image/')) {
            return (
                <div className="flex justify-center items-center h-full">
                    <img
                        src={fileUrl}
                        alt={fileName}
                        className="max-h-[80vh] max-w-full object-contain rounded-lg"
                    />
                </div>
            );
        }

        if (fileType.startsWith('video/')) {
            return (
                <div className="flex justify-center items-center h-full">
                    <video
                        controls
                        className="max-h-[80vh] max-w-full rounded-lg"
                    >
                        <source src={fileUrl} type={fileType} />
                        Your browser does not support the video tag.
                    </video>
                </div>
            );
        }

        if (fileType === 'application/pdf') {
            return (
                <div className="w-full h-full min-h-[60vh]">
                    <iframe
                        src={`${fileUrl}#toolbar=0`}
                        className="w-full h-full rounded-lg border border-gray-200"
                        title={fileName}
                    />
                </div>
            );
        }

        // Fallback for other files
        return (
            <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="bg-gray-100 p-6 rounded-full mb-4">
                    <Download className="w-12 h-12 text-gray-500" />
                </div>
                <h3 className="text-xl font-semibold text-gray-800 mb-2">
                    Preview not available
                </h3>
                <p className="text-gray-500 mb-6 max-w-sm">
                    This file type ({fileType}) cannot be previewed directly in the browser.
                </p>
                <a
                    href={fileUrl}
                    download={fileName}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 px-6 py-2.5 bg-primary hover:bg-primary-hover text-white rounded-lg font-medium transition-colors"
                >
                    <Download className="w-4 h-4" />
                    Download File
                </a>
            </div>
        );
    };

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[60] p-0 md:p-4 backdrop-blur-sm">
            {/* Close button outside - Adjusted for mobile */}
            <button
                onClick={onClose}
                className="absolute top-2 right-2 md:top-4 md:right-4 p-2 bg-black/50 text-white rounded-full hover:bg-white/20 transition-colors z-[70]"
            >
                <X className="w-5 h-5 md:w-6 md:h-6" />
            </button>

            <div className="bg-white rounded-none md:rounded-xl shadow-2xl w-full max-w-5xl h-full md:h-auto md:max-h-[90vh] flex flex-col overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between px-4 py-3 md:px-6 md:py-4 border-b border-gray-100 bg-gray-50 flex-shrink-0">
                    <div className="flex items-center gap-2 md:gap-3 overflow-hidden mr-8 md:mr-0">
                        <h3 className="font-semibold text-gray-800 truncate text-sm md:text-base" title={file.file_name || file.name}>
                            {file.file_name || file.name}
                        </h3>
                        <span className="text-xs text-gray-500 whitespace-nowrap px-2 py-0.5 bg-gray-200 rounded-full flex-shrink-0">
                            {(file.file_type || file.type).split('/')[1]?.toUpperCase() || 'FILE'}
                        </span>
                    </div>
                    <div className="flex items-center gap-2 hidden md:flex">
                        {(file.file_url || file instanceof File) && (
                            <a
                                href={file.file_url || (file instanceof File ? URL.createObjectURL(file) : '#')}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 text-gray-500 hover:bg-gray-200 rounded-lg transition-colors"
                                title="Open in new tab"
                            >
                                <ExternalLink className="w-5 h-5" />
                            </a>
                        )}
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-auto bg-gray-100 p-2 md:p-4 relative">
                    {/* Mobile External Link (if needed, or just rely on embedded) */}
                    <div className="md:hidden absolute top-2 right-2 z-10">
                        {(file.file_url || file instanceof File) && (
                            <a
                                href={file.file_url || (file instanceof File ? URL.createObjectURL(file) : '#')}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="p-2 bg-white/80 backdrop-blur-sm text-gray-700 rounded-lg shadow-sm border border-gray-200"
                            >
                                <ExternalLink className="w-5 h-5" />
                            </a>
                        )}
                    </div>
                    {getFileContent()}
                </div>
            </div>
        </div>
    );
};

export default FileViewer;
